import { useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

import { Heading, RadioGroup } from '@restworld/ui-ds-base';
import { useNotificationFrequencyOptions } from '@restworld/utils-common';

import { notificationModalStateAtom, SaveResearchFrequency, notificationChangeAtom } from './save-research-tab.atoms';
import SavedResearchUnSubscribeReason from './saved-research-unsubscribe-reason';
import { useSavedResearchProps } from './save-research-tab.hooks';

const circleIcon = <CheckCircleIcon className="text-green-700 w-[42px] h-[42px]" />;
export const SavedResearchNotificationModalContent = () => {
  const notificationFrequencyOptions = useNotificationFrequencyOptions();
  const { t: commonT } = useTranslation('common');
  const notificationOptions = useMemo(
    () => [
      ...notificationFrequencyOptions,
      ...[
        {
          id: 'unsubscribe',
          key: 'unsubscribe',
          label: commonT('stop_receiving_ads'),
        },
      ],
    ],
    [notificationFrequencyOptions, commonT]
  );

  const [notificationModalState] = useAtom(notificationModalStateAtom);
  const [notificationChange, setNotificationChange] = useAtom(notificationChangeAtom);
  const { title: headerTitle } = useSavedResearchProps();
  const { t } = useTranslation('user-settings');
  const contentEl = useMemo(() => {
    switch (notificationModalState) {
      case 'unsubscribe_review_success':
        return (
          <div className="flex flex-col gap-8 items-center justify-center">
            {circleIcon}
            <div className="flex flex-col gap-2 items-center justify-center">
              <Heading size={3} align="center" className="leading-[28.8px]">
                {t('unsubscribed_notifications_for_this_search_unsubscribed')}
              </Heading>
              <Heading size={5} align="center" weight="normal" color="muted" className="leading-[24px]">
                {t('update_preferences_when_signing_up_later')}
              </Heading>
            </div>
          </div>
        );
      case 'unsubscribe_user_review':
        return <SavedResearchUnSubscribeReason />;
      case 'frequency_change_success':
        return (
          <div className="flex flex-col gap-8 items-center justify-center">
            {circleIcon}
            <Heading size={3} align="center" className="leading-[28.8px] mb-7">
              {t('preferences_have_been_saved')}
            </Heading>
          </div>
        );
      case 'delete_save_research_success':
        return (
          <div className="flex flex-col gap-8 items-center justify-center mb-7">
            {circleIcon}
            <Heading size={3} align="center" className="leading-[28.8px]">
              {`${t('deleted_the_search', { title: `"${headerTitle}"` })}`}
            </Heading>
          </div>
        );
      default:
        return (
          <div className="flex flex-col gap-8">
            <Heading size={5} color="muted-foreground" weight="normal" className="leading-6">
              {t('select_notification_frequency')}
            </Heading>
            <RadioGroup
              value={notificationChange}
              id="notification_frequency"
              options={notificationOptions}
              onChange={(value) => {
                setNotificationChange(value as SaveResearchFrequency);
              }}
              returnKey="key"
              testId="notification_frequency"
            />
          </div>
        );
    }
  }, [headerTitle, notificationChange, notificationModalState, notificationOptions, setNotificationChange, t]);
  return contentEl;
};
export default SavedResearchNotificationModalContent;
